import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useState, useRef } from 'react';

import {
    IconBurger,
    IconBurgerDesktop,
    IconStapleFront,
    IconTitle
} from '$partials/icons/index.js';

import { linearScale } from '$utilities/scale.js';
import { Ticker } from '$partials/global/index.js';
import NavigationDialog from './navigation-dialog.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {React.FC<PropTypes.InferProps<typeof propTypes>>}
 */
const Header = forwardRef(({
    contact,
    headerHeight = () => { },
    links,
    watchedElemRef,
    pageType,
    quickLink1,
    quickLink2,
    tickItems
}, ref) => {
    // references
    const headerRef = useRef();
    const targetRef = useRef();
    const stapleRef = useRef();
    const rafScrollStapleRef = useRef();
    const rafScrollStickyRef = useRef();

    // state
    const [ isNavOpen, setIsNavOpen ] = useState(false);
    const [ isSticky, setIsSticky ] = useState(false);
    const [ stapleTransScale, setStapleTransScale ] = useState();
    const [ stapleTransTop, setStapleTransTop ] = useState();

    // methods
    const toggleNav = () => {
        setIsNavOpen((prev) => !prev);
    };

    const handleResize = () => {
        headerHeight(headerRef.current.offsetHeight);
    };

    const handleStapleScroll = () => {
        if (targetRef.current && watchedElemRef?.current && stapleRef?.current) {
            // first condition: fixed header
            const targetBottomPos = targetRef.current.getBoundingClientRect().bottom;
            const watchedElmTopPos = watchedElemRef.current.getBoundingClientRect().top;
            const watchedElmAbsTopPos = watchedElmTopPos + window.scrollY;

            if (pageType === 'Home') {
                if (window.innerWidth < 768) {
                    // mobile
                    const offset = 80;

                    const scaleVal = linearScale(window.scrollY, 0, (watchedElmAbsTopPos - targetBottomPos) - offset, 1, 2)
                    const scaleValInvert = 3 - scaleVal;

                    const topVal = linearScale(window.scrollY, 0, (watchedElmAbsTopPos - targetBottomPos) - offset, 65, 110)
                    const topValInvert = 175 - topVal;

                    if (stapleTransScale !== scaleValInvert) {
                        setStapleTransScale(scaleValInvert);
                    }

                    if (stapleTransTop !== topValInvert) {
                        setStapleTransTop(topValInvert);
                    }
                } else if (window.innerWidth < 1024) {
                    // tablet
                    const offset = 60;

                    const scaleVal = linearScale(window.scrollY, 0, (watchedElmAbsTopPos - targetBottomPos) - offset, 0.8, 1.5)
                    const scaleValInvert = 2.3 - scaleVal;

                    const topVal = linearScale(window.scrollY, 0, (watchedElmAbsTopPos - targetBottomPos) - offset, 80, 110)
                    const topValInvert = 190 - topVal;

                    if (stapleTransScale !== scaleValInvert) {
                        setStapleTransScale(scaleValInvert);
                    }

                    if (stapleTransTop !== topValInvert) {
                        setStapleTransTop(topValInvert);
                    }
                } else {
                    // desktop
                    const offset = 50;

                    const scaleVal = linearScale(window.scrollY, 0, (watchedElmAbsTopPos - targetBottomPos) - offset, 0.2, 1)
                    const scaleValInvert = 1.2 - scaleVal;

                    const topVal = linearScale(window.scrollY, 0, (watchedElmAbsTopPos - targetBottomPos) - offset, 97, 175)
                    const topValInvert = 272 - topVal;

                    if (stapleTransScale !== scaleValInvert) {
                        setStapleTransScale(scaleValInvert);
                    }

                    if (stapleTransTop !== topValInvert) {
                        setStapleTransTop(topValInvert);
                    }
                }
            } else {
                if (window.innerWidth < 768) {
                    // mobile
                    const scaleVal = linearScale(watchedElmTopPos, 330, 360, 1, 1)
                    const scaleValInvert = scaleVal;

                    const topVal = linearScale(watchedElmTopPos, 330, 360, 64, 64)
                    const topValInvert = topVal;

                    if (stapleTransScale !== scaleValInvert) {
                        setStapleTransScale(scaleValInvert);
                    }

                    if (stapleTransTop !== topValInvert) {
                        setStapleTransTop(topValInvert);
                    }
                } else if (window.innerWidth < 1024) {
                    // tablet
                    const scaleVal = linearScale(watchedElmTopPos, 120, 174, 0.7, 1)
                    const scaleValInvert = scaleVal;

                    const topVal = linearScale(watchedElmTopPos, 120, 174, 84, 84)
                    const topValInvert = topVal;

                    if (stapleTransScale !== scaleValInvert) {
                        setStapleTransScale(scaleValInvert);
                    }

                    if (stapleTransTop !== topValInvert) {
                        setStapleTransTop(topValInvert);
                    }
                }  else {
                    // desktop
                    const offset = 60;

                    const scaleVal = linearScale(window.scrollY, 0, (watchedElmAbsTopPos - targetBottomPos) - offset, 0.2, 0.39)
                    const scaleValInvert = 0.59 - scaleVal;

                    const topVal = linearScale(window.scrollY, 0, (watchedElmAbsTopPos - targetBottomPos) - offset, 96, 100)
                    const topValInvert = 196 - topVal;

                    if (stapleTransScale !== scaleValInvert) {
                        setStapleTransScale(scaleValInvert);
                    }

                    if (stapleTransTop !== topValInvert) {
                        setStapleTransTop(topValInvert);
                    }
                }
            }
        } else if (targetRef.current && stapleRef?.current) {
            // second condition: non-fixed header
            const targetBottomPos = targetRef.current.getBoundingClientRect().bottom;

            if (window.innerWidth < 768) {
                // mobile
                const scaleVal = linearScale(window.scrollY, 0, targetBottomPos, 1, 1)
                const scaleValInvert = scaleVal;

                const topVal = linearScale(window.scrollY, 0, targetBottomPos, 65, 65)
                const topValInvert = topVal;

                if (stapleTransScale !== scaleValInvert) {
                    setStapleTransScale(scaleValInvert);
                }

                if (stapleTransTop !== topValInvert) {
                    setStapleTransTop(topValInvert);
                }
            } else if (window.innerWidth < 1024) {
                // tablet
                const scaleVal = linearScale(window.scrollY, 0, targetBottomPos, 0.7, 0.7)
                const scaleValInvert = scaleVal;

                const topVal = linearScale(window.scrollY, 0, targetBottomPos, 85, 85)
                const topValInvert = topVal;

                if (stapleTransScale !== scaleValInvert) {
                    setStapleTransScale(scaleValInvert);
                }

                if (stapleTransTop !== topValInvert) {
                    setStapleTransTop(topValInvert);
                }
            }  else {
                // desktop
                const scaleVal = linearScale(window.scrollY, 0, targetBottomPos, 0.2, 0.39)
                const scaleValInvert = 0.59 - scaleVal;

                const topVal = linearScale(window.scrollY, 0, targetBottomPos, 96, 100)
                const topValInvert = 196 - topVal;

                if (stapleTransScale !== scaleValInvert) {
                    setStapleTransScale(scaleValInvert);
                }

                if (stapleTransTop !== topValInvert) {
                    setStapleTransTop(topValInvert);
                }
            }
        }

        rafScrollStapleRef.current = requestAnimationFrame(handleStapleScroll);
    };

    const handleStickyScroll = () => {
        if (targetRef.current && watchedElemRef?.current) {
            // first condition: fixed header
            const targetBottomPos = targetRef.current.getBoundingClientRect().bottom;
            const watchedElemTopPos = watchedElemRef.current.getBoundingClientRect().top;

            if (watchedElemTopPos <= targetBottomPos) {
                if (!isSticky) { setIsSticky(true); };
            } else {
                if (isSticky) { setIsSticky(false); };
            }
        } else if (targetRef.current) {
            // second condition: non-fixed header
            const offset = 20;

            const navTopPos = targetRef.current.getBoundingClientRect().top;

            if (navTopPos <= offset) {
                if (!isSticky) { setIsSticky(true); };
            } else {
                if (isSticky) { setIsSticky(false); };
            }
        }

        rafScrollStickyRef.current = requestAnimationFrame(handleStickyScroll);
    };

    // hooks
    useEffect(() => {
        window.addEventListener('resize', handleResize, { passive: true });

        handleResize();
        handleStapleScroll();
        handleStickyScroll();
        return () => {
            window.removeEventListener('resize', handleResize);

            cancelAnimationFrame(rafScrollStapleRef.current);
            cancelAnimationFrame(rafScrollStickyRef.current);
        }
    }, [ isSticky ]);

    // misc
    const navClass = isSticky ? ['opacity-0 transition-opacity duration-300'] : ['transition-opacity duration-300'];
    switch (pageType) {
    case 'ArticleListingPage':
    case 'CalendarListingPage':
    case 'CommunityListingPage':
    case 'DisciplineDetailPage':
    case 'FaqPage':
    case 'GlossaryPage':
    case 'Home':
    case 'JobListingPage':
    case 'LandingPage':
    case 'ProgrammeListingPage':
    case 'RegionDetailPage':
    case 'TopicDetailPage':
        navClass.push('sticky-nav');
        break;
    default:
        break;
    }

    const paddingBottomPages = ['Home', 'ProgrammeDetailPage', 'PersonDetailPage', 'AuthorityDetailPage', 'ArticleDetailPage'];
    const paddingBottom = paddingBottomPages.includes(pageType);

    return (
        <>
            <header
                className={navClass.join(' ')}
                inert={isSticky ? 'true' : null}
                ref={headerRef}>
                {tickItems?.length > 0 && (
                    <Ticker items={tickItems} />
                )}

                <div className={`relative layout-container pb-20 sm:pb-40 ${pageType === 'Home' ? 'md:pb-60' : 'md:pb-0'}`}>
                    <div className='flex justify-end pt-20 sm:pt-40 md:pt-50'>
                        <nav
                            aria-label='Quick links and navigation'
                            className={`flex justify-end md:items-center ${paddingBottom ? 'md:mb-50' : 'md:mb-0 '}`}
                            ref={targetRef}>
                            <div className='type-style-4 hidden md:block'>
                                <ul className='md:flex'>
                                    {quickLink1 && (
                                        <li>
                                            <a
                                                className='py-12 md:mr-20'
                                                href={quickLink1.url}>
                                                {quickLink1.title}
                                            </a>
                                        </li>
                                    )}

                                    {quickLink2 && (
                                        <li>
                                            <a
                                                className='py-12 md:mr-20'
                                                href={quickLink2.url}>
                                                {quickLink2.title}
                                            </a>
                                        </li>
                                    )}
                                </ul>
                            </div>

                            <button
                                aria-expanded={isNavOpen}
                                aria-label='Open main navigation'
                                id='primary-navigation'
                                onClick={toggleNav}
                                type='button'>
                                <span className='md:hidden'>
                                    <IconBurger/>
                                </span>

                                <span className='hidden md:block'>
                                    <IconBurgerDesktop/>
                                </span>
                            </button>

                            {/* Dialog */}
                            <NavigationDialog
                                contact={contact}
                                handleClose={toggleNav}
                                isNavOpen={isNavOpen}
                                links={links}
                                pageType={pageType} />
                        </nav>
                    </div>

                    {pageType === 'Home' && (
                        <div className='w-full flex justify-center mt-20 sm:mt-0'>
                            <IconTitle />
                        </div>
                    )}
                </div>

                <a
                    aria-label='Goto home page'
                    className='absolute left-20 sm:left-40 md:left-60'
                    href='/'
                    ref={stapleRef}
                    style={{
                        top: `${stapleTransTop}px`,
                        transform: `scale(${stapleTransScale})`,
                        transformOrigin: '0 0'
                    }}>
                    <IconStapleFront className='icon-staple--home'/>
                </a>
            </header>

            {/* fixed header w/ no react ref  */}
            <header
                className={`fixed top-0 right-0 left-0 z-50 bg-white border-b border-black transition-opacity duration-300${isSticky ? ' opacity-100' : ' opacity-0'}`}
                inert={isSticky ? null : 'true'}>
                <div className='relative layout-container'>
                    <div className='flex justify-between items-center py-10'>
                        <a
                            aria-label='Goto home page'
                            href='/'>
                            <IconStapleFront className='icon-staple--small'/>
                        </a>

                        <nav
                            aria-label='Quick links and navigation'
                            className='flex justify-end md:items-center'>
                            <div className='type-style-4 hidden md:block'>
                                <ul className='md:flex'>
                                    {quickLink1 && (
                                        <li>
                                            <a
                                                className='py-12 md:mr-20'
                                                href={quickLink1.url}>
                                                {quickLink1.title}
                                            </a>
                                        </li>
                                    )}

                                    {quickLink2 && (
                                        <li>
                                            <a
                                                className='py-12 md:mr-20'
                                                href={quickLink2.url}>
                                                {quickLink2.title}
                                            </a>
                                        </li>
                                    )}
                                </ul>
                            </div>

                            <button
                                aria-expanded={isNavOpen}
                                aria-label='Open main navigation'
                                id='primary-navigation'
                                onClick={toggleNav}
                                type='button'>
                                <span className='md:hidden'>
                                    <IconBurger/>
                                </span>

                                <span className='hidden md:block'>
                                    <IconBurgerDesktop/>
                                </span>
                            </button>

                            {/* Dialog */}
                            <NavigationDialog
                                contact={contact}
                                handleClose={toggleNav}
                                isNavOpen={isNavOpen}
                                links={links}
                                pageType={pageType} />
                        </nav>
                    </div>
                </div>
            </header>
        </>
    );
});

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

Header.displayName = 'Header';

const propTypes = {
    contact: PropTypes.string,
    headerHeight: PropTypes.func,
    links: PropTypes.array,
    pageType: PropTypes.string,
    quickLink1: PropTypes.object,
    quickLink2: PropTypes.object,
    tickItems: PropTypes.array,
    watchedElemRef: PropTypes.object
};

Header.propTypes = propTypes;

export default Header;
